/**
 *
 * Copyright (c) 2019-present, wobe-systems GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * to be found in the LICENSE file in the root directory of this source tree.
 */

import {
  httpClient, BASE_URL, PORTS, SERVICES, PATH_EXTENSION,
} from '@/middlewares/httpClient';

import { InstalledBrick, Port } from '@/store/bricks/models';
import {
  Flow, Configuration, Connection, FlowRunConfig, BrickInFlow,
} from './models';

const key: string = 'flowmanager';
const port: string = PORTS.get(key);
const service: string = SERVICES.get(key);

export const FLOW_MANAGER: string = `${BASE_URL}:${port}${PATH_EXTENSION}/${service}`;

const GetFlows = (id: string) => httpClient
  .get(`${FLOW_MANAGER}/workspaces/${id}/flows`);

const GetFlow = (flow: Flow) => httpClient
  .get(`${FLOW_MANAGER}/flows/${flow}`);

const DeleteFlow = (flowName: string) => httpClient
  . delete(`${FLOW_MANAGER}/flows/${flowName}`);

const PostFlow = (flowName: string, workspaceId: string) => httpClient
  .post(`${FLOW_MANAGER}/flows/`, { name: flowName, workspaceId });

const PutFlow = (flow: Flow) => httpClient
  .put(`${FLOW_MANAGER}/flows/${flow.id}`, flow);

const EditFlowName = (flowID: string, newName: string, workspaceId: string) => httpClient
  .put(`${FLOW_MANAGER}/flows/editname`, { id: flowID, name: newName, workspaceId });

const EditFlowDescription = (
  flowID: string, newDescription: string, workspaceId: string,
) => httpClient
  .put(`${FLOW_MANAGER}/flows/editdescription`, { id: flowID, description: newDescription, workspaceId });

const CopyFlow = (oldFlow: string, newFlow: string, workspaceId: string) => httpClient
  .post(`${FLOW_MANAGER}/flows/copy`, [{ id: oldFlow, workspaceId }, { name: newFlow }]);

const DecryptParameter = (flowID: string, brickID: string, parameterName: string) => httpClient
  .get(`${FLOW_MANAGER}/flows/${flowID}/decrypt_parameter/${brickID}/${parameterName}`);

const CreateConnection = (configID: string, connection:Connection) => httpClient
  .post(`${FLOW_MANAGER}/connections/${configID}`, connection);

const GetConnections = (configID:string) => httpClient
  .get(`${FLOW_MANAGER}/connections/${configID}`);

const DeleteConnection = (configID: string, connectionID: string) => httpClient
  .delete(`${FLOW_MANAGER}/connections/${configID}/${connectionID}`);

const UpdateConnection = (configID: string, connection:Connection) => httpClient
  .put(`${FLOW_MANAGER}/connections/${configID}`, connection);

const AddBrick = (brick: InstalledBrick) => httpClient
  .post(`${FLOW_MANAGER}/bricks`, brick);

const UpdateBrick = (brick: BrickInFlow) => httpClient
  .put(`${FLOW_MANAGER}/bricks`, brick);

const DeleteBrick = (brickId: string) => httpClient
  .delete(`${FLOW_MANAGER}/bricks/${brickId}`);

const GRID_MANAGER: string = `${BASE_URL}:${PORTS
  .get('gridmanager')}${PATH_EXTENSION}/${SERVICES.get('gridmanager')}`;

const StartFlow = (flowId: string, flowConfig: FlowRunConfig) => httpClient
  .put(`${GRID_MANAGER}/flows/start/${flowId}`, { ...flowConfig });

const StopFlow = (flowId: string, flowConfig: FlowRunConfig) => httpClient
  .put(`${GRID_MANAGER}/flows/stop/${flowId}`, { ...flowConfig });

const GetFlowState = (FlowId: string) => httpClient
  .get(`${GRID_MANAGER}/flows/state/${FlowId}`);

const GetFlowsState = () => httpClient
  .get(`${GRID_MANAGER}/flows`);

const CreatePortJSON = (cport : Port) => httpClient
  .post(`${FLOW_MANAGER}/ports/create_json`, cport);

const ConfigureAPIBrick = (
  apiId: string, endpointId: string, brickId:string,
) => httpClient
  .put(`${FLOW_MANAGER}/bricks/${brickId}/configure`, { endpointId, apiId });

const ExportFlow = (flowId: string) => httpClient
  .get(`${FLOW_MANAGER}/flows/${flowId}/export`);
const ImportFlow = (workspaceId: string, files: any) => httpClient
  .post(`${FLOW_MANAGER}/workspaces/${workspaceId}/flows/import`, files);

export {
  GetFlows,
  GetFlow,
  DeleteFlow,
  PostFlow,
  PutFlow,
  CopyFlow,
  EditFlowName,
  EditFlowDescription,
  StartFlow,
  StopFlow,
  GetFlowState,
  GetFlowsState,
  CreatePortJSON,
  DecryptParameter,
  CreateConnection,
  GetConnections,
  DeleteConnection,
  UpdateConnection,
  AddBrick,
  UpdateBrick,
  DeleteBrick,
  ConfigureAPIBrick,
  ExportFlow,
  ImportFlow,
};
